import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { pageRect } from "@/lib/density";

/**
 * Înlocuitor pentru <Select>, cu aceeași interfață (value, onChange(e) cu e.target.value, <option> ca
 * și copii). Până la PRAG opțiuni rămâne un <Select> obișnuit; peste prag devine o casetă în care
 * se poate scrie și care filtrează lista (fără diacritice, fără majuscule, toate cuvintele
 * scrise trebuie să se regăsească în denumire — ex. „resurse umane”, cod COR, parte din nume).
 */
const PRAG = 8;
const MAX_AFISATE = 100;

const norm = (s) => String(s ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const textOf = (c) => React.Children.toArray(c).map(x => (typeof x === "object" ? textOf(x.props?.children) : String(x))).join("");

function parseOptions(children) {
  const out = [];
  const walk = (nodes) => React.Children.forEach(nodes, (el) => {
    if (!React.isValidElement(el)) return;
    if (el.type === "option") {
      const label = textOf(el.props.children);
      out.push({ value: el.props.value !== undefined ? String(el.props.value) : label, label, disabled: !!el.props.disabled });
    } else if (el.props?.children) walk(el.props.children);
  });
  walk(children);
  return out;
}

export default function Select({ children, value, onChange, className, style, disabled, name, id, ...rest }) {
  const options = useMemo(() => parseOptions(children), [children]);
  if (options.length <= PRAG) {
    return <select className={className} style={style} value={value} onChange={onChange} disabled={disabled} name={name} id={id} {...rest}>{children}</select>;
  }
  return <SearchSelect options={options} value={value} onChange={onChange} className={className} style={style}
    disabled={disabled} name={name} id={id} {...rest} />;
}

function SearchSelect({ options, value, onChange, className, style, disabled, name, id, ...rest }) {
  const cur = String(value ?? "");
  const selected = options.find(o => o.value === cur);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(null);   // null = nu se scrie, se vede valoarea aleasă
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const tokens = norm(query).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return options;
    return options.filter(o => o.value !== "" && tokens.every(t => norm(o.label).includes(t)));
  }, [options, query]);
  const shown = filtered.slice(0, MAX_AFISATE);

  const place = () => {
    const r = inputRef.current && pageRect(inputRef.current);
    if (!r) return;
    const below = r.vh - r.bottom - 8, above = r.top - 8;
    const up = below < 200 && above > below;
    setPos({ left: r.left, width: r.width, ...(up ? { bottom: r.vh - r.top + 2, maxHeight: Math.min(300, above) }
                                                  : { top: r.bottom + 2, maxHeight: Math.min(300, below) }) });
  };
  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open]);
  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const close = () => { setOpen(false); setQuery(null); };
  const pick = (o) => {
    if (!o || o.disabled) return;
    onChange?.({ target: { value: o.value, name }, currentTarget: { value: o.value, name } });
    close();
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); if (!open) setOpen(true); else setActive(a => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { if (open) { e.preventDefault(); pick(shown[active]); } }
    else if (e.key === "Escape") { if (open) { e.stopPropagation(); close(); } }
    else if (e.key === "Tab") close();
  };

  return (
    <div style={{ position: "relative", width: style?.width, ...(style?.flex ? { flex: style.flex } : {}) }}>
      <input ref={inputRef} type="text" role="combobox" aria-expanded={open} aria-autocomplete="list" autoComplete="off"
        className={className} id={id} name={name} disabled={disabled} {...rest}
        style={{ ...style, width: "100%", paddingRight: 26, textOverflow: "ellipsis" }}
        value={query !== null ? query : (selected?.label ?? "")}
        placeholder={open && query === null ? "Scrie pentru a căuta…" : undefined}
        onFocus={(e) => { setOpen(true); e.target.select(); }}
        onClick={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onBlur={close}
        onKeyDown={onKeyDown} />
      <span aria-hidden="true" style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", color: "#64748B", fontSize: 10, pointerEvents: "none" }}>▾</span>
      {open && pos && createPortal(
        <div ref={listRef} role="listbox"
          style={{ position: "fixed", zIndex: 1000, left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight,
                   overflow: "auto", background: "white", border: "1px solid #E2E8F0", borderRadius: 6,
                   boxShadow: "0 8px 24px rgba(15,23,42,.18)", padding: 4 }}
          onMouseDown={(e) => e.preventDefault()}>
          {shown.length === 0 && <div style={{ padding: "6px 8px", fontSize: 12, color: "#64748B" }}>Niciun rezultat.</div>}
          {shown.map((o, i) => (
            <div key={o.value + "|" + i} role="option" aria-selected={o.value === cur} data-idx={i}
              onMouseEnter={() => setActive(i)} onClick={() => pick(o)}
              style={{ padding: "6px 8px", fontSize: 13, cursor: o.disabled ? "not-allowed" : "pointer", borderRadius: 4,
                       background: i === active ? "var(--sky-100)" : "transparent", color: o.disabled ? "#94A3B8" : "var(--navy)",
                       fontWeight: o.value === cur ? 700 : 400, whiteSpace: "normal" }}>
              {o.label || "—"}
            </div>
          ))}
          {filtered.length > MAX_AFISATE && (
            <div style={{ padding: "6px 8px", fontSize: 11, color: "#64748B" }}>
              Se afișează primele {MAX_AFISATE} din {filtered.length} — continuă să scrii pentru a restrânge.
            </div>
          )}
        </div>, document.body)}
    </div>
  );
}
